<template>
  <v-dialog
    v-model="isOpen"
    max-width="480"
    scrollable
    data-test="task-link-dialog"
  >
    <v-card class="task-link-card">
      <v-card-title class="task-link-card__title">
        <v-icon left small color="primary">mdi-clipboard-arrow-right-outline</v-icon>
        <span class="task-link-card__title-text" :title="dialogTitle">{{ dialogTitle }}</span>
        <v-btn icon small class="task-link-card__close" @click="onClose" data-test="task-link-close" title="Close">
          <v-icon small>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-divider />

      <v-card-text class="task-link-card__body">
        <!-- Board (task type) first, then a task within that board -->
        <v-autocomplete
          v-model="selectedBoardId"
          :items="boards"
          item-text="name"
          item-value="id"
          label="Select a task board"
          placeholder="Search boards…"
          :loading="loadingBoards"
          outlined
          dense
          hide-details
          clearable
          class="mb-3"
          data-test="task-link-board-select"
          :menu-props="{ offsetY: true }"
          @change="onBoardChange"
        />

        <!-- Picker: choose a task within the board + link -->
        <div class="task-link-picker">
          <v-autocomplete
            v-model="selectedTaskId"
            :items="selectableTasks"
            item-text="label"
            item-value="id"
            label="Select a task"
            :placeholder="selectedBoardId ? 'Search tasks…' : 'Pick a board first'"
            :no-data-text="selectedBoardId ? 'No tasks on this board' : 'Pick a board first'"
            :loading="loadingTasks"
            :disabled="!selectedBoardId"
            outlined
            dense
            hide-details
            clearable
            data-test="task-link-select"
            :menu-props="{ offsetY: true }"
          />
          <v-btn
            color="primary"
            depressed
            :disabled="!selectedTaskId || linking"
            :loading="linking"
            class="task-link-picker__btn"
            @click="onLink"
            data-test="task-link-add"
          >
            <v-icon left small>mdi-plus</v-icon>
            Link
          </v-btn>
        </div>

        <v-divider class="my-3" />

        <!-- Linked tasks -->
        <div class="task-link-list">
          <div class="task-link-list__heading">
            Linked tasks
            <span class="task-link-list__count">{{ linkedLinks.length }}</span>
          </div>

          <div v-if="loadingLinks" class="task-link-list__state">
            <v-progress-circular indeterminate size="18" width="2" color="primary" />
            <span>Loading…</span>
          </div>

          <div
            v-else-if="linkedLinks.length === 0"
            class="task-link-list__state task-link-list__state--empty"
            data-test="task-link-empty"
          >
            No tasks linked yet.
          </div>

          <v-list v-else dense class="task-link-list__items">
            <v-list-item
              v-for="link in linkedLinks"
              :key="link.id"
              class="task-link-list__item"
              :data-test="`task-link-item-${link.id}`"
            >
              <v-list-item-icon class="task-link-list__item-icon">
                <v-icon small color="primary">mdi-clipboard-check-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title>{{ link.taskName || link.taskId }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action>
                <v-btn
                  icon
                  x-small
                  color="error"
                  :disabled="unlinkingId === link.id"
                  :loading="unlinkingId === link.id"
                  @click="onUnlink(link)"
                  :data-test="`task-link-unlink-${link.id}`"
                  title="Unlink"
                >
                  <v-icon x-small>mdi-close</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
import { log as debugLog, error as debugError } from '@/utils/debugLogger';
import ganttService from '@/features/Task/service/gantt.service';
import {
  listCmlTaskLinks,
  linkTaskScope,
  unlinkCmlTask,
} from './cmlTaskLinks';

/**
 * TaskLinkDialog — Wave C-ι demo slice. A lightweight v-dialog (deliberately
 * NOT the premium slide-out used by CmlAddEditPanel) for linking an AppTask to
 * a CmlRecord and managing existing links. Parent-owned open state via v-model,
 * mirroring the other monitoring dialogs.
 */
export default {
  name: 'TaskLinkDialog',

  props: {
    value: { type: Boolean, default: false },
    cmlRecordId: { type: String, default: '' },
    cmlNo: { type: String, default: '' },
    // Optional narrowing — when set, the dialog links/lists at this exact scope
    // (TML point or thickness reading) rather than the whole CML.
    tmlPointId: { type: String, default: '' },
    thicknessReadingId: { type: String, default: '' },
    // Human breadcrumb shown in the title (e.g. "CR-002 › Top, 12 o'clock").
    scopeLabel: { type: String, default: '' },
  },

  data() {
    return {
      allTasks: [],
      linkedLinks: [],
      selectedTaskId: null,
      selectedBoardId: null,
      loadingBoards: false,
      loadingTasks: false,
      loadingLinks: false,
      linking: false,
      unlinkingId: null,
    };
  },

  computed: {
    // Boards (AppTaskType) are populated app-wide via TASK_MANAGER/GET_BY_USER
    // (App.vue + layouts). Read-only here; ensureBoards() guard-loads if empty.
    boards() {
      return this.$store.getters['TASK_MANAGER/GET_ENTRIES_BY_USER'] || [];
    },
    // Full header text — bound to the title attr too so the scope is readable
    // on hover when the span truncates.
    dialogTitle() {
      return `Link Task to ${this.scopeLabel || this.cmlNo || 'CML'}`;
    },
    isOpen: {
      get() { return this.value; },
      set(v) { this.$emit('input', v); },
    },
    /**
     * Tasks not already linked to this CML, so the picker can't create a
     * duplicate. (The server is idempotent regardless — this is just cleaner UX.)
     */
    selectableTasks() {
      const linkedIds = new Set(this.linkedLinks.map((l) => l.taskId));
      return this.allTasks.filter((t) => !linkedIds.has(t.id));
    },
  },

  watch: {
    value(open) {
      if (open && this.cmlRecordId) {
        this.selectedBoardId = null;
        this.selectedTaskId = null;
        this.allTasks = [];
        this.ensureBoards();
        this.loadLinks();
      }
    },
  },

  methods: {
    // Boards are populated app-wide on load (App.vue / layouts dispatch
    // TASK_MANAGER/GET_BY_USER). Guard-dispatch in the rare case the getter is
    // still empty when the dialog opens.
    async ensureBoards() {
      if (this.boards.length > 0) return;
      this.loadingBoards = true;
      try {
        await this.$store.dispatch('TASK_MANAGER/GET_BY_USER');
      } catch (err) {
        debugError('[TaskLinkDialog] ensureBoards failed', err);
      } finally {
        this.loadingBoards = false;
      }
    },

    onBoardChange() {
      this.selectedTaskId = null;
      this.allTasks = [];
      if (this.selectedBoardId) this.loadTasksForBoard();
    },

    async loadTasksForBoard() {
      this.loadingTasks = true;
      try {
        const res = await ganttService.get({ typeId: this.selectedBoardId });
        this.allTasks = this.normalizeTasks(res && res.data);
      } catch (err) {
        debugError('[TaskLinkDialog] loadTasksForBoard failed', err);
        this.notify('Could not load tasks for this board.', 'error');
        this.allTasks = [];
      } finally {
        this.loadingTasks = false;
      }
    },

    async loadLinks() {
      this.loadingLinks = true;
      try {
        // ListForCml returns every link under the CML; filter to THIS scope so
        // each dialog instance manages only its own depth (CML / TML / reading).
        const all = await listCmlTaskLinks(this.cmlRecordId);
        const tml = this.tmlPointId || '';
        const reading = this.thicknessReadingId || '';
        this.linkedLinks = (all || []).filter(
          (l) => (l.tmlPointId || '') === tml && (l.thicknessReadingId || '') === reading,
        );
      } catch (err) {
        debugError('[TaskLinkDialog] loadLinks failed', err);
        this.notify('Could not load linked tasks.', 'error');
      } finally {
        this.loadingLinks = false;
      }
    },

    /**
     * Normalize the board-scoped /api/tasks response into { id, label }.
     * TaskVM serializes the title as `text` (TaskVM.cs JsonProperty), so read
     * `text` first; fall back to name/friendlyId/id only when the title is blank.
     */
    normalizeTasks(data) {
      const list = Array.isArray(data) ? data : (data && data.items) || [];
      return list
        .map((t) => ({
          id: t.id || t.taskId,
          label: t.text || t.name || t.title || t.friendlyId || t.id,
        }))
        .filter((t) => t.id);
    },

    async onLink() {
      if (!this.selectedTaskId || this.linking) return;
      this.linking = true;
      try {
        await linkTaskScope(this.cmlRecordId, {
          taskId: this.selectedTaskId,
          tmlPointId: this.tmlPointId || null,
          thicknessReadingId: this.thicknessReadingId || null,
        });
        debugLog('[TaskLinkDialog] linked', {
          cmlRecordId: this.cmlRecordId,
          taskId: this.selectedTaskId,
          tmlPointId: this.tmlPointId || null,
          thicknessReadingId: this.thicknessReadingId || null,
        });
        this.selectedTaskId = null;
        await this.loadLinks();
        this.notify('Task linked.', 'success');
      } catch (err) {
        debugError('[TaskLinkDialog] link failed', err);
        this.notify(this.errMsg(err, 'Could not link task.'), 'error');
      } finally {
        this.linking = false;
      }
    },

    async onUnlink(link) {
      if (!link || this.unlinkingId) return;
      this.unlinkingId = link.id;
      try {
        await unlinkCmlTask(this.cmlRecordId, link.id);
        debugLog('[TaskLinkDialog] unlinked', { linkId: link.id });
        await this.loadLinks();
        this.notify('Task unlinked.', 'success');
      } catch (err) {
        debugError('[TaskLinkDialog] unlink failed', err);
        this.notify(this.errMsg(err, 'Could not unlink task.'), 'error');
      } finally {
        this.unlinkingId = null;
      }
    },

    onClose() {
      this.isOpen = false;
      this.$emit('close');
    },

    errMsg(err, fallback) {
      return (err && err.response && err.response.data && err.response.data.message) || fallback;
    },

    notify(message, type = 'info') {
      if (this.$dialog && this.$dialog.notify && this.$dialog.notify[type]) {
        this.$dialog.notify[type](message, { position: 'top-right', timeout: 3000 });
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.task-link-card__title {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  font-size: 16px;
  font-weight: 600;
  padding: 14px 16px;
}
.task-link-card__title-text {
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.task-link-card__close {
  flex: 0 0 auto;
}
.task-link-card__body {
  padding: 16px !important;
}
.task-link-picker {
  display: flex;
  align-items: flex-start;
  gap: 8px;
}
.task-link-picker__btn {
  text-transform: none;
  letter-spacing: 0.01em;
  height: 40px;
  flex: 0 0 auto;
}
.task-link-list__heading {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(0, 0, 0, 0.6);
  margin-bottom: 4px;
}
.task-link-list__count {
  background: rgba(0, 0, 0, 0.06);
  border-radius: 10px;
  padding: 0 7px;
  font-size: 11px;
}
.task-link-list__state {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: rgba(0, 0, 0, 0.55);
  padding: 10px 4px;
}
.task-link-list__state--empty {
  font-style: italic;
}
.task-link-list__item-icon {
  margin-right: 8px !important;
  align-self: center;
}
</style>
